import { useMemo, useState } from 'react';
import { parseCard, skatGameValue, skatIsTrump, skatMatadors, skatPower, SKAT_BASE, type SkatGame, type SkatLegal, type SkatView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { seatPosition, SeatView, MySeat, useNarrow, useSpeech } from './common';

const OFFSETS: Record<string, { x: number; y: number }> = {
  bottom: { x: 0, y: 34 },
  left: { x: -62, y: 0 },
  top: { x: 0, y: -34 },
  right: { x: 62, y: 0 },
};

const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_ORDER: Record<string, number> = { C: 0, S: 1, H: 2, D: 3 };
const GAME_ORDER: SkatGame[] = ['C', 'S', 'H', 'D', 'GRAND', 'NULL'];
const isRedSuit = (s: string) => s === 'H' || s === 'D';

/** Jacks and trumps first, then the other suits, strongest first (Null: plain suit order). */
function sortSkat(hand: readonly string[], game: SkatGame | null): string[] {
  const g: SkatGame = game ?? 'GRAND';
  const rank = (c: string) => -skatPower(c, g);
  const group = (c: string) => (skatIsTrump(c, g) ? -1 : (SUIT_ORDER[parseCard(c).suit] ?? 9));
  return [...hand].sort((a, b) => group(a) - group(b) || rank(a) - rank(b));
}

export function SkatTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as SkatView;
  const legal = useGame((s) => s.legal) as SkatLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const speech = useSpeech();
  const [selected, setSelected] = useState<string | null>(null);
  const [discard, setDiscard] = useState<string[]>([]);
  const [pick, setPick] = useState<SkatGame>('C');
  const hand = useMemo(() => sortSkat(view.hand, view.game), [view.hand, view.game]);
  const playable = new Set(legal?.kind === 'play' ? legal.cards : []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const narrow = useNarrow();
  const trickSize = narrow ? 'md' : 'lg';
  const k = narrow ? 0.7 : 1;
  const bidding = view.phase === 'BIDDING';
  const declaring = view.phase === 'DECLARE' && myTurn && legal?.kind === 'declare';
  const gameLabel = (g: SkatGame) => (g === 'GRAND' || g === 'NULL' ? t(`skt.game.${g}`) : SUIT_SYMBOL[g]!);
  const lastCall = (s: number) => view.bidding?.history.filter((h) => h.seat === s).at(-1);
  const callTag = (s: number) => {
    const c = lastCall(s);
    if (!bidding || !c) return null;
    return c.kind === 'pass' ? t('ta.pass') : c.kind === 'hold' ? t('skt.holds') : String(c.value);
  };

  const onCard = (c: string) => {
    if (!myTurn) return;
    if (declaring) {
      setDiscard((d) => (d.includes(c) ? d.filter((x) => x !== c) : d.length < 2 ? [...d, c] : [d[1]!, c]));
      return;
    }
    if (legal?.kind !== 'play' || !playable.has(c)) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  const last = view.handResults.at(-1);
  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2].map((s) => (
          <SeatView
            key={s}
            seat={s}
            info={seats[s]}
            mySeat={mySeat}
            total={3}
            turn={turn}
            speech={speech[s]}
            sub={<span className="dim num">{view.scores[s] ?? 0} pts</span>}
            tags={
              <>
                {view.dealer === s && <span className="tag">D</span>}
                {view.declarer === s && <span className="tag">{t('skt.declarer')}</span>}
                {callTag(s) && <span className="tag">{callTag(s)}</span>}
                {!bidding && view.phase !== 'SKAT' && view.phase !== 'DECLARE' && (
                  <span className="tag">{t('skt.cardPts', { n: view.trickPoints[s] ?? 0 })}</span>
                )}
              </>
            }
          />
        ))}
        <div className="felt-center centered">
          <div className="center-note">
            {t('skt.deal', { n: view.round })} ·{' '}
            {view.game ? (
              <>
                {t('skt.playing')}{' '}
                <b style={{ color: isRedSuit(view.game) ? 'var(--danger)' : undefined, fontSize: '1.2em' }}>{gameLabel(view.game)}</b> · {t('skt.bid')} {view.bid}
              </>
            ) : bidding ? (
              <>
                {t('skt.bid')} {view.bidding?.current ? view.bidding.current : '—'}
              </>
            ) : (
              <>
                {t('skt.bid')} {view.bid}
              </>
            )}
          </div>
          {(bidding || view.phase === 'SKAT' || view.phase === 'DECLARE') && (
            <div className="slot" aria-label={t('skt.skat')} style={{ position: 'relative', display: 'grid', placeItems: 'center', gap: 4 }}>
              <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                {view.skatCount > 0 ? (
                  Array.from({ length: view.skatCount }, (_, i) => <PlayingCard key={i} faceDown size={trickSize} />)
                ) : (
                  <span className="pill">{t('skt.skatTaken')}</span>
                )}
              </div>
              <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{view.skatCount > 0 ? t('skt.skat') : ''}</span>
              {bidding && view.bidding && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.bidding', { name: name(view.turn) })}</span>}
              {view.phase === 'SKAT' && !myTurn && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.looking', { name: name(view.turn) })}</span>}
              {view.phase === 'DECLARE' && !myTurn && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.choosing', { name: name(view.turn) })}</span>}
              {bidding && view.passedOut > 0 && <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.redealt')}</span>}
            </div>
          )}
          {(view.phase === 'PLAYING' || view.phase === 'TRICK_END') && (
            <div className={`trick ${view.phase === 'TRICK_END' ? 'collect' : ''}`} key={`trick-${view.round}-${view.tricks.length}-${view.phase === 'TRICK_END'}`} aria-label="Cards on the table">
              {view.trick.map((p, i) => {
                const dir = seatPosition(p.seat, mySeat, 3).dir;
                const o = OFFSETS[dir]!;
                const off = { x: o.x * k, y: o.y * k };
                const w = view.phase === 'TRICK_END' && view.lastTrick ? OFFSETS[seatPosition(view.lastTrick.winner, mySeat, 3).dir]! : null;
                const winnerDir = w ? { x: w.x * k, y: w.y * k } : null;
                return (
                  <div
                    key={p.card}
                    className="slot fly"
                    style={{
                      transform: `translate(calc(-50% + ${off.x}px), calc(-50% + ${off.y}px)) rotate(${(i - 1) * 4}deg)`,
                      ['--from-x' as string]: `${off.x * 3}px`,
                      ['--from-y' as string]: `${off.y * 4}px`,
                      ...(winnerDir ? { ['--to-x' as string]: `${winnerDir.x * 3}px`, ['--to-y' as string]: `${winnerDir.y * 4}px`, animationDelay: '0.45s' } : {}),
                      zIndex: i,
                    }}
                  >
                    <PlayingCard id={p.card} size={trickSize} />
                  </div>
                );
              })}
            </div>
          )}
          {view.phase === 'HAND_END' && last && (
            <div className="card glass" style={{ textAlign: 'center' }}>
              <strong>{t('skt.handScores', { n: view.round })}</strong>
              <div style={{ marginTop: 6 }}>
                {t(last.won ? 'skt.won' : 'skt.lost', { name: name(last.declarer) })} · {gameLabel(last.game)} · {t('skt.declarerPoints', { n: last.declarerPoints })}
              </div>
              <div className="row wrap" style={{ justifyContent: 'center', marginTop: 6 }}>
                <span className={`pill ${last.score > 0 ? 'accent' : ''}`}>{last.score > 0 ? '+' : ''}{last.score}</span>
                {last.game !== 'NULL' && <span className="pill">{t(last.matadors.with ? 'skt.with' : 'skt.without', { n: last.matadors.count })}</span>}
                {last.schneider && <span className="pill">{t('skt.schneider')}</span>}
                {last.schwarz && <span className="pill">{t('skt.schwarz')}</span>}
              </div>
              {last.overbid && <div className="dim" style={{ marginTop: 4 }}>{t('skt.overbid', { value: last.value, bid: last.bid })}</div>}
            </div>
          )}
        </div>
        <MySeat
          seat={mySeat !== null ? seats[mySeat] : undefined}
          sub={mySeat !== null ? <>{view.scores[mySeat] ?? 0} pts{bidding || view.phase === 'SKAT' || view.phase === 'DECLARE' ? '' : ` · ${t('wh.tricks', { n: view.tricksWon[mySeat] ?? 0 })}`}</> : null}
        />
      </div>
      {myTurn && legal?.kind === 'bid' && <BidPicker legal={legal} onBid={(value) => void act({ type: 'bid', value })} onPass={() => void act({ type: 'pass' })} />}
      {myTurn && legal?.kind === 'respond' && <HoldPicker current={legal.current} onHold={() => void act({ type: 'hold' })} onPass={() => void act({ type: 'pass' })} />}
      {myTurn && legal?.kind === 'pickup' && (
        <div className="bid-picker" role="group" aria-label={t('skt.pickup')}>
          <div className="bid-head">
            <strong>{t('skt.youWon', { n: view.bid })}</strong>
          </div>
          <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.skatHint')}</span>
          <button className="btn primary block" onClick={() => void act({ type: 'pickup' })}>
            {t('skt.pickup')}
          </button>
        </div>
      )}
      {declaring && legal?.kind === 'declare' && (
        <DeclarePicker
          hand={view.hand}
          bid={legal.bid}
          discard={discard}
          pick={pick}
          onPick={setPick}
          label={gameLabel}
          onConfirm={() => {
            const [a, b] = discard;
            if (a && b) void act({ type: 'declare', game: pick, discard: [a, b] }).then(() => setDiscard([]));
          }}
        />
      )}
      <div className="table-bottom">
        <Hand size="lg" label="Your hand">
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size="lg"
              dealIndex={view.tricks.length === 0 && view.trick.length === 0 && view.phase === 'BIDDING' ? i : undefined}
              selected={selected === c || (declaring && discard.includes(c))}
              playable={myTurn && (declaring ? true : playable.has(c))}
              unplayable={myTurn && legal?.kind === 'play' && !playable.has(c)}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              {[0, 1, 2].map((s) => (
                <th key={s} className="ellipsis" style={{ maxWidth: 90 }}>{name(s)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {view.handResults.map((row, r) => (
              <tr key={r}>
                <td>D{r + 1}</td>
                {[0, 1, 2].map((s) => (
                  <td key={s} className="num">
                    {s === row.declarer ? (
                      <>
                        {row.score > 0 ? `+${row.score}` : row.score}
                        <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
                          {gameLabel(row.game)} · {row.bid}
                          {row.schwarz ? ` · ${t('skt.schwarz')}` : row.schneider ? ` · ${t('skt.schneider')}` : ''}
                        </div>
                      </>
                    ) : (
                      '·'
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td><strong>Total</strong></td>
              {view.scores.map((v, s) => (
                <td key={s} className="num"><strong>{v}</strong></td>
              ))}
            </tr>
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('skt.scoreHint', { target: view.config.targetScore })}</p>
      </Modal>
    </>
  );
}

function useSendLock(): [boolean, (fn: () => void) => void] {
  const [sent, setSent] = useState(false);
  return [
    sent,
    (fn) => {
      setSent(true);
      fn();
      setTimeout(() => setSent(false), 1500);
    },
  ];
}

function BidPicker({ legal, onBid, onPass }: { legal: Extract<SkatLegal, { kind: 'bid' }>; onBid: (value: number) => void; onPass: () => void }) {
  const t = useT();
  const [sent, send] = useSendLock();
  const [all, setAll] = useState(false);
  const values = all ? legal.values.slice(0, 42) : legal.values.slice(0, 13);
  return (
    <div className="bid-picker" role="group" aria-label={t('skt.bid')}>
      <div className="bid-head">
        <strong>{t(legal.role === 'forehand' ? 'skt.forehandTitle' : 'skt.bidTitle')}</strong>
        <span className="dim">{legal.current > 0 ? t('skt.current', { n: legal.current }) : ''}</span>
      </div>
      <div className="bid-chips more">
        {values.map((v) => (
          <button key={v} className="bid-chip" style={{ fontSize: 'var(--fs-sm)' }} disabled={sent} aria-label={`${t('skt.bid')} ${v}`} onClick={() => send(() => onBid(v))}>
            {v}
          </button>
        ))}
        {!all && legal.values.length > values.length && (
          <button className="bid-chip plus" disabled={sent} aria-label={t('skt.more')} onClick={() => setAll(true)}>
            +
          </button>
        )}
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
          {t('ta.pass')}
        </button>
      </div>
    </div>
  );
}

function HoldPicker({ current, onHold, onPass }: { current: number; onHold: () => void; onPass: () => void }) {
  const t = useT();
  const [sent, send] = useSendLock();
  return (
    <div className="bid-picker" role="group" aria-label={t('skt.respondTitle')}>
      <div className="bid-head">
        <strong>{t('skt.respondTitle')}</strong>
        <span className="dim">{t('skt.current', { n: current })}</span>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn primary grow" disabled={sent} onClick={() => send(onHold)}>
          {t('skt.hold', { n: current })}
        </button>
        <button className="btn grow" disabled={sent} onClick={() => send(onPass)}>
          {t('ta.pass')}
        </button>
      </div>
    </div>
  );
}

function DeclarePicker({
  hand,
  bid,
  discard,
  pick,
  onPick,
  label,
  onConfirm,
}: {
  hand: string[];
  bid: number;
  discard: string[];
  pick: SkatGame;
  onPick: (g: SkatGame) => void;
  label: (g: SkatGame) => string;
  onConfirm: () => void;
}) {
  const t = useT();
  const [sent, send] = useSendLock();
  const value = skatGameValue(pick, skatMatadors(hand, pick).count);
  return (
    <div className="bid-picker" role="group" aria-label={t('skt.declareTitle')}>
      <div className="bid-head">
        <strong>{t('skt.declareTitle')}</strong>
        <span className="dim">{t('skt.discardCount', { n: discard.length })}</span>
      </div>
      <div className="bid-chips more" role="group" aria-label={t('skt.chooseGame')}>
        {GAME_ORDER.map((g) => (
          <button
            key={g}
            className={`bid-chip ${pick === g ? 'on' : ''}`}
            style={{ color: pick !== g && isRedSuit(g) ? 'var(--danger)' : undefined, fontSize: g === 'GRAND' || g === 'NULL' ? 'var(--fs-xs)' : '1.4em' }}
            aria-pressed={pick === g}
            aria-label={`${t('skt.chooseGame')}: ${t(`skt.game.${g}`)}`}
            onClick={() => onPick(g)}
          >
            {label(g)}
          </button>
        ))}
      </div>
      <span className={value < bid ? 'error' : 'dim'} style={{ fontSize: 'var(--fs-xs)' }}>
        {value < bid ? t('skt.worthLess', { value, bid }) : t('skt.worth', { value, base: SKAT_BASE[pick] })}
      </span>
      <button className="btn primary block" disabled={sent || discard.length !== 2} onClick={() => send(onConfirm)}>
        {t('skt.confirm', { game: t(`skt.game.${pick}`) })}
      </button>
    </div>
  );
}
